/**
 * Railing — Phase 3, multi-host extended 2026-08-25. A free line-hosted rail: posts at
 * the type's spacing along the drawn `path`, a continuous top rail between them.
 *
 * EXPLICIT HOST PICK round: a railing's host is never inferred from where it's drawn
 * (a railing always draws freestanding, flat at baseOffsetFt) — Revit-style, hosts are
 * chosen deliberately afterward via the pet palette's "Host to…" action.
 *
 * MULTI-HOST round: a railing may host onto ANY NUMBER of stairs/ramps/floors/topo at
 * once (`hostIds` below), not just one — the real case is a single rail run that
 * starts on a stair, crosses a landing slab, and continues along a deck's edge, or a
 * rail that follows a graded terrain edge. Each station along the rail (every post +
 * rail-segment endpoint) takes its z from whichever attached host geometrically owns
 * that point — see `railing/generate3d.ts` for the per-station ownership rule. Topo
 * is deliberately the lowest-priority fallback (it covers the whole site); a station
 * no host claims falls back to the railing's own flat baseOffsetFt. See
 * docs/MODELER.md "RAILING EXPLICIT HOST round" and "RAILING MULTI-HOST round".
 */
import type { CategorySchema } from '../../registry'
import { GUARD_KINDS, GUARD_INFILLS, GUARD_RAILS, GUARD_NEWELS } from '../../stairGuard'

export const railingSchema: CategorySchema = {
  id: 'railing',
  pathParamKey: 'path',
  label: 'Railing',
  hostable: { hostsInto: ['stair', 'ramp', 'floor', 'topo'] },
  typeParams: {
    groups: [
      {
        label: 'Appearance',
        params: [
          // P6b: '' = the built-in default look (pre-P6b behavior)
          { key: 'surfaceMaterial', label: 'Material', kind: 'material', default: '' },
        ],
      },
      {
        label: 'Construction',
        params: [
          {
            // GLASS RAILING round (2026-09-30): the railing's construction family.
            // 'posts' = the original posts + top rail (default — every pre-round
            // type reads it); 'glassShoe' = structural glass panels in a continuous
            // aluminum base shoe (the CR Laurence Taper-Loc / base-shoe system).
            key: 'railStyle', label: 'Style', kind: 'choice', default: 'posts',
            choices: [
              { value: 'posts', label: 'Posts & Top Rail' },
              { value: 'glassShoe', label: 'Glass on Base Shoe' },
              // SHOWER CURB round (2026-10-02, Matt: "a shower curb with the option to
              // turn on/off glass and add a parametric glass door to it that we can move
              // and flip … drawn in a line on the set level")
              { value: 'showerCurb', label: 'Shower Curb & Glass' },
              // STAIR STUDIO (2026-10-10): the guard catalog — infill, handrail, newels — as a
              // firm-standard TYPE, on a deck, a balcony, a floor opening or a stair
              { value: 'guard', label: 'Guard (infill, handrail, newels)' },
            ],
          },
          {
            key: 'heightFt', label: 'Height', kind: 'length', default: 3, min: 2, max: 5,
            conditionalOn: { param: 'railStyle', equals: ['posts', 'glassShoe'] },
          },
          {
            key: 'postSpacingFt', label: 'Post Spacing', kind: 'length', default: 4, min: 1, max: 8,
            conditionalOn: { param: 'railStyle', equals: 'posts' },
          },
        ],
      },
      {
        // the guard catalog's look (railStyle 'guard'): built by bim-geometry railing/stairGuard.ts
        label: 'Guard',
        params: [
          { key: 'guardKind', label: 'Kind', kind: 'choice', default: 'guard', choices: GUARD_KINDS.map((x) => ({ value: x.value, label: x.label })), conditionalOn: { param: 'railStyle', equals: 'guard' } },
          { key: 'guardInfill', label: 'Infill', kind: 'choice', default: 'balusters', choices: GUARD_INFILLS.map((x) => ({ value: x.value, label: x.label })), conditionalOn: { param: 'railStyle', equals: 'guard' } },
          { key: 'guardRail', label: 'Handrail', kind: 'choice', default: 'round', choices: GUARD_RAILS.map((x) => ({ value: x.value, label: x.label })), conditionalOn: { param: 'railStyle', equals: 'guard' } },
          { key: 'guardNewel', label: 'Newels', kind: 'choice', default: 'box', choices: GUARD_NEWELS.map((x) => ({ value: x.value, label: x.label })), conditionalOn: { param: 'railStyle', equals: 'guard' } },
          { key: 'guardMount', label: 'Mounting', kind: 'choice', default: 'top', choices: [{ value: 'top', label: 'On Top' }, { value: 'fascia', label: 'On the Side (Fascia)' }], conditionalOn: { param: 'railStyle', equals: 'guard' } },
          { key: 'guardHeightIn', label: 'Guard Height (in)', kind: 'number', default: 36, min: 34, max: 48, conditionalOn: { param: 'railStyle', equals: 'guard' } },
          { key: 'railHeightIn', label: 'Handrail Height on a Stair (in)', kind: 'number', default: 36, min: 34, max: 38, conditionalOn: { param: 'railStyle', equals: 'guard' } },
          { key: 'railMaterial', label: 'Handrail Material', kind: 'material', default: '', conditionalOn: { param: 'railStyle', equals: 'guard' } },
          { key: 'infillMaterial', label: 'Infill Material', kind: 'material', default: '', conditionalOn: { param: 'railStyle', equals: 'guard' } },
          { key: 'newelMaterial', label: 'Newel Material', kind: 'material', default: '', conditionalOn: { param: 'railStyle', equals: 'guard' } },
        ],
      },
      {
        // GLASS RAILING round: every value here is a firm standard on the TYPE —
        // panels lay out per straight run of the drawn path, equal widths no wider
        // than maxPanelWidthFt, panelGapFt between neighbors; the shoe runs
        // continuous end to end, mitered at every corner.
        label: 'Glass & Shoe',
        params: [
          {
            key: 'maxPanelWidthFt', label: 'Max Panel Width', kind: 'length', default: 4, min: 1, max: 8,
            conditionalOn: { param: 'railStyle', equals: 'glassShoe' },
          },
          {
            key: 'panelGapFt', label: 'Gap Between Panels', kind: 'length', default: 1 / 12, min: 0, max: 0.5,
            conditionalOn: { param: 'railStyle', equals: 'glassShoe' },
          },
          {
            key: 'glassThicknessFt', label: 'Glass Thickness', kind: 'length', default: 0.5 / 12, min: 0.25 / 12, max: 1.5 / 12,
            conditionalOn: { param: 'railStyle', equals: ['glassShoe', 'showerCurb'] },
          },
          {
            key: 'shoeWidthFt', label: 'Shoe Width', kind: 'length', default: 2.5 / 12, min: 1 / 12, max: 6 / 12,
            conditionalOn: { param: 'railStyle', equals: 'glassShoe' },
          },
          {
            key: 'shoeHeightFt', label: 'Shoe Height', kind: 'length', default: 4.1875 / 12, min: 1 / 12, max: 8 / 12,
            conditionalOn: { param: 'railStyle', equals: 'glassShoe' },
          },
          {
            key: 'capWidthFt', label: 'Cap Rail Width', kind: 'length', default: 1.75 / 12, min: 0.75 / 12, max: 6 / 12,
            conditionalOn: { param: 'railStyle', equals: 'glassShoe' },
          },
          {
            key: 'capHeightFt', label: 'Cap Rail Height', kind: 'length', default: 1.5 / 12, min: 0.5 / 12, max: 4 / 12,
            conditionalOn: { param: 'railStyle', equals: 'glassShoe' },
          },
          // '' = the built-in clear-glass / clear-anodized looks
          {
            key: 'glassMaterial', label: 'Glass Material', kind: 'material', default: '',
            conditionalOn: { param: 'railStyle', equals: ['glassShoe', 'showerCurb'] },
          },
          {
            key: 'shoeMaterial', label: 'Shoe & Cap Material', kind: 'material', default: '',
            conditionalOn: { param: 'railStyle', equals: 'glassShoe' },
          },
        ],
      },
      {
        // SHOWER CURB round: the curb runs the whole drawn line; fixed glass stands on
        // it, the door is one panel of it (instance: where, how wide, which way)
        label: 'Shower Curb',
        params: [
          {
            key: 'curbWidthFt', label: 'Curb Width', kind: 'length', default: 4.5 / 12, min: 2 / 12, max: 12 / 12,
            conditionalOn: { param: 'railStyle', equals: 'showerCurb' },
          },
          {
            key: 'curbHeightFt', label: 'Curb Height', kind: 'length', default: 4 / 12, min: 1 / 12, max: 12 / 12,
            conditionalOn: { param: 'railStyle', equals: 'showerCurb' },
          },
          {
            key: 'showerGlassHeightFt', label: 'Glass Height (above curb)', kind: 'length', default: 6, min: 3, max: 9,
            conditionalOn: { param: 'railStyle', equals: 'showerCurb' },
          },
          {
            key: 'curbMaterial', label: 'Curb Material', kind: 'material', default: '',
            conditionalOn: { param: 'railStyle', equals: 'showerCurb' },
          },
          {
            key: 'hardwareMaterial', label: 'Hardware Finish (hinges, clips, pull, frame)', kind: 'material', default: '',
            conditionalOn: { param: 'railStyle', equals: 'showerCurb' },
          },
        ],
      },
    ],
  },
  instanceParams: {
    groups: [
      {
        label: 'Constraints',
        params: [
          { key: 'path', label: 'Path', kind: 'path2d', default: null },
          { key: 'baseOffsetFt', label: 'Base Offset', kind: 'length', default: 0, min: -20, max: 20 },
          // PAINT BRUSH (2026-10-07): the brushed color (a colors.ts ref) — blank = its finish / role
          { key: 'paintColor', label: 'Color', kind: 'data', default: null, hidden: true },
          {
            // GLASS RAILING round: a cap rail over the glass, per railing (the same
            // glass type runs capped on one deck and frameless on another). Railing
            // height stays the top of the whole assembly either way.
            key: 'capRail', label: 'Cap Rail', kind: 'bool', default: false,
            conditionalOn: { param: 'railStyle', equals: 'glassShoe' },
          },
          {
            // MULTI-HOST round: the PICKED elements (stair/ramp/floor/topo ids) this
            // railing hosts onto, any number at once — the wall attach model
            // (topAttachIds). Managed by the pet palette's "Host to…" pick flow, never
            // a form field; positional (a relationship to what's under THIS railing —
            // derivation never auto-couples). Empty/null = freestanding, flat at
            // baseOffsetFt. A pre-multi-host doc's legacy single record-level `hostId`
            // is migrated into this array once on doc open
            // (elementCommands.migrateRailingHostToHostIds) and read as a one-item
            // fallback at resolve time even before that (registry.ts).
            key: 'hostIds', label: 'Host Targets', kind: 'data',
            default: null, positional: true, hidden: true,
          },
        ],
      },
      {
        // STAIR STUDIO stage 5 (2026-10-09): a GUARD placed by the Stair Studio — with a
        // `stairSide` and a stair among its hosts, the railing builds its line from the stair
        // (stairGuard.ts) instead of its own path: nosing line on the flights, level on the landings.
        // The look is the railing's TYPE (railStyle 'guard', Matt 2026-10-10: "why can't it just be
        // the type selector?"); the keys below are the Studio's per-stair overrides of that type,
        // blank = the type's. Edited in the Studio only, never in the palette.
        label: 'Stair Guard',
        params: [
          {
            key: 'stairSide', label: 'Stair Side', kind: 'choice', default: '', hidden: true,
            choices: [{ value: '', label: 'None (follows its own line)' }, { value: 'left', label: 'Left (going up)' }, { value: 'right', label: 'Right (going up)' }],
          },
          { key: 'studioStairId', label: 'Studio Stair', kind: 'data', default: null, hidden: true },
          { key: 'guardKind', label: 'Kind', kind: 'choice', default: '', hidden: true, choices: [{ value: '', label: 'From the Type' }, ...GUARD_KINDS.map((x) => ({ value: x.value, label: x.label }))] },
          { key: 'guardInfill', label: 'Infill', kind: 'choice', default: '', hidden: true, choices: [{ value: '', label: 'From the Type' }, ...GUARD_INFILLS.map((x) => ({ value: x.value, label: x.label }))] },
          { key: 'guardRail', label: 'Handrail', kind: 'choice', default: '', hidden: true, choices: [{ value: '', label: 'From the Type' }, ...GUARD_RAILS.map((x) => ({ value: x.value, label: x.label }))] },
          { key: 'guardNewel', label: 'Newels', kind: 'choice', default: '', hidden: true, choices: [{ value: '', label: 'From the Type' }, ...GUARD_NEWELS.map((x) => ({ value: x.value, label: x.label }))] },
          { key: 'guardMount', label: 'Mounting', kind: 'choice', default: '', hidden: true, choices: [{ value: '', label: 'From the Type' }, { value: 'top', label: 'On the Treads' }, { value: 'fascia', label: 'On the Side (Fascia)' }] },
          { key: 'railHeightIn', label: 'Handrail Height (in)', kind: 'number', default: null, hidden: true },
          { key: 'guardHeightIn', label: 'Guard Height (in)', kind: 'number', default: null, hidden: true },
          { key: 'railMaterial', label: 'Handrail Material', kind: 'material', default: '', hidden: true },
          { key: 'infillMaterial', label: 'Infill Material', kind: 'material', default: '', hidden: true },
          { key: 'newelMaterial', label: 'Newel Material', kind: 'material', default: '', hidden: true },
          // STAIR STUDIO stage 6: an LED channel under the handrail
          { key: 'railLed', label: 'LED in the Handrail', kind: 'bool', default: false, hidden: true },
        ],
      },
      {
        // SHOWER CURB round: per placement — glass on/off, the door on/off, its width,
        // where it sits along the curb (the door grip; null = centered), which end it
        // hinges on and which side of the drawn line it swings to (the Flip buttons)
        label: 'Shower Glass',
        params: [
          {
            key: 'showerGlass', label: 'Glass', kind: 'bool', default: true,
            conditionalOn: { param: 'railStyle', equals: 'showerCurb' },
          },
          {
            key: 'showerDoor', label: 'Door', kind: 'bool', default: true,
            conditionalOn: [{ param: 'railStyle', equals: 'showerCurb' }, { param: 'showerGlass', equals: true }],
          },
          {
            key: 'doorWidthFt', label: 'Door Width', kind: 'length', default: 2, min: 1.25, max: 3.5,
            conditionalOn: [{ param: 'railStyle', equals: 'showerCurb' }, { param: 'showerGlass', equals: true }, { param: 'showerDoor', equals: true }],
          },
          {
            // SHOWER GLASS HARDWARE round (2026-10-07, Matt: "show the parameters in the
            // properties pallet so the user can be exact"): the door's near edge, measured
            // along the line from its START — empty = centered on the longest run. The
            // door grip writes the same value; the door never folds round a corner.
            key: 'doorOffsetFt', label: 'Door Position (from start)', kind: 'length', default: null,
            optional: true, optionalHint: 'centered', min: 0, max: 100,
            conditionalOn: [{ param: 'railStyle', equals: 'showerCurb' }, { param: 'showerGlass', equals: true }, { param: 'showerDoor', equals: true }],
          },
          {
            key: 'doorHinge', label: 'Hinge Edge', kind: 'choice', default: 'start',
            choices: [{ value: 'start', label: 'Edge toward the line start' }, { value: 'end', label: 'Edge toward the line end' }],
            conditionalOn: [{ param: 'railStyle', equals: 'showerCurb' }, { param: 'showerGlass', equals: true }, { param: 'showerDoor', equals: true }],
          },
          {
            key: 'doorSwing', label: 'Swings To', kind: 'choice', default: 'right',
            choices: [{ value: 'left', label: 'Left of the drawn line' }, { value: 'right', label: 'Right of the drawn line' }],
            conditionalOn: [{ param: 'railStyle', equals: 'showerCurb' }, { param: 'showerGlass', equals: true }, { param: 'showerDoor', equals: true }],
          },
        ],
      },
      {
        // SHOWER GLASS HARDWARE round (2026-10-07, Matt: "hardware settings including the
        // ability to go frameless or full frame, and on frameless show the typical CRL
        // style hinges and a ladder pull … glass mounting clips should also be included").
        // Frameless = clamp hinges (wall-mount at a line end, glass-to-glass beside a
        // fixed panel, 90 degree at a corner), U-clips holding every fixed panel to the
        // curb, the wall and its neighbor across a turn, and a back-to-back ladder pull.
        // Full frame = sill, jambs, header and corner posts in the hardware finish, the
        // door in its own frame on a continuous hinge.
        label: 'Shower Hardware',
        params: [
          {
            key: 'showerFraming', label: 'Framing', kind: 'choice', default: 'frameless',
            choices: [{ value: 'frameless', label: 'Frameless (clamps & clips)' }, { value: 'framed', label: 'Full frame' }],
            conditionalOn: [{ param: 'railStyle', equals: 'showerCurb' }, { param: 'showerGlass', equals: true }],
          },
          {
            key: 'pullLengthFt', label: 'Ladder Pull Length', kind: 'length', default: 18 / 12, min: 6 / 12, max: 4,
            conditionalOn: [{ param: 'railStyle', equals: 'showerCurb' }, { param: 'showerGlass', equals: true }, { param: 'showerDoor', equals: true }],
          },
          {
            key: 'pullHeightFt', label: 'Pull Center Height', kind: 'length', default: 3.5, min: 2, max: 6,
            conditionalOn: [{ param: 'railStyle', equals: 'showerCurb' }, { param: 'showerGlass', equals: true }, { param: 'showerDoor', equals: true }],
          },
        ],
      },
    ],
  },
}
